import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useGetFleetOverview } from '../../../api/routes/fleet'
import { Badge } from '../../../components/ui/badge'
import { Input } from '../../../components/ui/input'
import {
    Monitor,
    AlertTriangle,
    Clock,
    Ruler,
    Gauge,
    LayoutGrid,
    List,
    ArrowUp,
    ArrowDown,
} from 'lucide-react'

// const statusColors: Record<string, string> = {
//     online: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
//     offline: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
//     inactive: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
// }

type SortField = 'name' | 'hours' | 'area' | 'errors'
type SortDir = 'asc' | 'desc'
type ViewMode = 'grid' | 'list'

const sortDevices = (devices: any[], field: SortField, dir: SortDir) => {
    const mul = dir === 'asc' ? 1 : -1
    return [...devices].sort((a, b) => {
        switch (field) {
            case 'name':
                return (
                    mul * (a.deviceName || '').localeCompare(b.deviceName || '')
                )
            case 'hours':
                return (
                    mul *
                    ((a.latestMetrics.machineRunHours ?? -1) -
                        (b.latestMetrics.machineRunHours ?? -1))
                )
            case 'area':
                return (
                    mul *
                    ((a.latestMetrics.totalAreaGround ?? -1) -
                        (b.latestMetrics.totalAreaGround ?? -1))
                )
            case 'errors':
                return mul * ((a.errorsToday ?? 0) - (b.errorsToday ?? 0))
            default:
                return 0
        }
    })
}

const defaultDir: Record<SortField, SortDir> = {
    name: 'asc',
    hours: 'desc',
    area: 'desc',
    errors: 'desc',
}

const OverviewSkeleton = () => (
    <div className="animate-pulse space-y-4">
        <div className="flex gap-3 h-10">
            <div className="h-10 bg-muted rounded-lg flex-1 max-w-xs" />
            <div className="h-10 bg-muted rounded-lg w-32" />
            <div className="h-10 bg-muted rounded-lg w-36" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...Array(6)].map((_, i) => (
                <div key={i} className="h-36 bg-muted rounded-lg" />
            ))}
        </div>
    </div>
)

const formatMetric = (value: number | null, digits: number) =>
    value != null
        ? value.toLocaleString(undefined, { maximumFractionDigits: digits })
        : '\u2014'

const DeviceCardGrid = ({ device }: { device: any }) => (
    <Link to={`/devices/${device.deviceId}/live`} className="block">
        <div className="rounded-lg border bg-card p-4 sm:p-5 hover:border-foreground/20 transition-colors duration-150 cursor-pointer h-full flex flex-col">
            <div className="flex items-start justify-between mb-3">
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground truncate">
                        {device.deviceName}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                        {device.modelName || 'Unknown Model'}
                        {device.businessName &&
                            ` \u00b7 ${device.businessName}`}
                    </p>
                </div>
                {/* <Badge
                    variant="secondary"
                    className={`text-xs flex-shrink-0 ml-2 ${statusColors[device.status] || ''}`}
                >
                    {device.status}
                </Badge> */}
            </div>
            <div className="grid grid-cols-3 gap-2">
                <div>
                    <div className="flex items-center gap-1 mb-0.5">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground uppercase">
                            Hours
                        </span>
                    </div>
                    <p className="text-sm font-medium text-foreground tabular-nums">
                        {formatMetric(device.latestMetrics.machineRunHours, 1)}
                    </p>
                </div>
                <div>
                    <div className="flex items-center gap-1 mb-0.5">
                        <Ruler className="h-3 w-3 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground uppercase">
                            Area
                        </span>
                    </div>
                    <p className="text-sm font-medium text-foreground tabular-nums">
                        {formatMetric(device.latestMetrics.totalAreaGround, 0)}
                    </p>
                </div>
                {/* <div>
                    <div className="flex items-center gap-1 mb-0.5">
                        <Gauge className="h-3 w-3 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground uppercase">
                            Prod
                        </span>
                    </div>
                    <p className="text-sm font-medium text-foreground tabular-nums">
                        {formatMetric(device.latestMetrics.productivity, 1)}
                    </p>
                </div> */}
            </div>
            <div className="flex items-center gap-1.5 mt-auto pt-3 border-t border-border/50">
                {device.errorsToday > 0 ? (
                    <>
                        <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                        <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                            {device.errorsToday} error
                            {device.errorsToday !== 1 ? 's' : ''} today
                        </span>
                    </>
                ) : (
                    <span className="text-xs text-muted-foreground">
                        No errors today
                    </span>
                )}
            </div>
        </div>
    </Link>
)

const listGridCols =
    'grid grid-cols-[1fr_5rem_5rem_6rem] sm:grid-cols-[1fr_5rem_5rem_6rem] gap-x-4 items-center'

const ListHeader = () => (
    <div className={`${listGridCols} px-4 py-2`}>
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
            Device
        </span>
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide text-right">
            Hours
        </span>
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide text-right">
            Area
        </span>
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide text-right">
            Errors
        </span>
    </div>
)

const DeviceCardList = ({ device }: { device: any }) => (
    <Link to={`/devices/${device.deviceId}/live`} className="block">
        <div
            className={`${listGridCols} rounded-lg border bg-card px-4 py-3 hover:border-foreground/20 transition-colors duration-150 cursor-pointer`}
        >
            <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">
                    {device.deviceName}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                    {device.modelName || 'Unknown Model'}
                    {device.businessName && ` \u00b7 ${device.businessName}`}
                </p>
            </div>
            <span className="text-sm font-medium text-foreground tabular-nums text-right">
                {formatMetric(device.latestMetrics.machineRunHours, 1)}
            </span>
            <span className="text-sm font-medium text-foreground tabular-nums text-right">
                {formatMetric(device.latestMetrics.totalAreaGround, 0)}
            </span>
            <span
                className={`text-xs font-medium text-right ${device.errorsToday > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}
            >
                {device.errorsToday > 0
                    ? `${device.errorsToday} error${device.errorsToday !== 1 ? 's' : ''}`
                    : 'No errors today'}
            </span>
        </div>
    </Link>
)

export const OverviewTab = () => {
    const { data, isLoading } = useGetFleetOverview()
    const [viewMode, setViewMode] = useState<ViewMode>('grid')
    const [search, setSearch] = useState('')
    const [sortField, setSortField] = useState<SortField>('name')
    const [sortDir, setSortDir] = useState<SortDir>('asc')

    const handleSortChange = (field: SortField) => {
        if (field === sortField) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
        } else {
            setSortField(field)
            setSortDir(defaultDir[field])
        }
    }

    const filteredDevices = useMemo(() => {
        if (!data?.devices) return []
        let devices = data.devices

        if (search) {
            const q = search.toLowerCase()
            devices = devices.filter(
                (d: any) =>
                    d.deviceName?.toLowerCase().includes(q) ||
                    d.modelName?.toLowerCase().includes(q) ||
                    d.businessName?.toLowerCase().includes(q)
            )
        }

        return sortDevices(devices, sortField, sortDir)
    }, [data?.devices, search, sortField, sortDir])

    if (isLoading) return <OverviewSkeleton />
    if (!data) return null

    return (
        <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-3">
                <Input
                    placeholder="Search devices..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="sm:max-w-xs h-9"
                />
                <div className="flex gap-3 flex-wrap items-center sm:ml-auto">
                    <span className="text-xs font-medium text-muted-foreground">
                        Sort by
                    </span>
                    <div className="flex items-center rounded-md border border-input overflow-hidden">
                        {(
                            [
                                ['name', 'Name'],
                                ['hours', 'Hours'],
                                ['area', 'Area'],
                                ['errors', 'Errors'],
                            ] as [SortField, string][]
                        ).map(([field, label]) => {
                            const active = sortField === field
                            const DirIcon =
                                active && sortDir === 'asc'
                                    ? ArrowUp
                                    : ArrowDown
                            return (
                                <button
                                    key={field}
                                    onClick={() => handleSortChange(field)}
                                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors border-r border-input last:border-r-0 ${
                                        active
                                            ? 'bg-accent text-accent-foreground'
                                            : 'bg-background text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground'
                                    }`}
                                >
                                    {label}
                                    {active && <DirIcon className="h-3 w-3" />}
                                </button>
                            )
                        })}
                    </div>
                    <div className="flex rounded-md border border-input overflow-hidden">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`px-2.5 py-1.5 transition-colors ${
                                viewMode === 'grid'
                                    ? 'bg-accent text-accent-foreground'
                                    : 'bg-background text-muted-foreground hover:bg-accent/50'
                            }`}
                        >
                            <LayoutGrid className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`px-2.5 py-1.5 border-l border-input transition-colors ${
                                viewMode === 'list'
                                    ? 'bg-accent text-accent-foreground'
                                    : 'bg-background text-muted-foreground hover:bg-accent/50'
                            }`}
                        >
                            <List className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Device count */}
            <p className="text-xs text-muted-foreground">
                {filteredDevices.length} device
                {filteredDevices.length !== 1 ? 's' : ''}
                {search && ` (filtered from ${data.devices.length})`}
            </p>

            {/* Device list */}
            {filteredDevices.length === 0 ? (
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <Monitor className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-medium text-foreground mb-1">
                        {data.devices.length === 0
                            ? 'No Devices'
                            : 'No Matches'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                        {data.devices.length === 0
                            ? 'No devices are available.'
                            : 'Try adjusting your search or filters.'}
                    </p>
                </div>
            ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredDevices.map((device: any) => (
                        <DeviceCardGrid key={device.deviceId} device={device} />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col gap-1.5">
                    <ListHeader />
                    {filteredDevices.map((device: any) => (
                        <DeviceCardList key={device.deviceId} device={device} />
                    ))}
                </div>
            )}
        </div>
    )
}
